import { EzrImage, Toaster } from "ui-widgets";
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Tooltip,
} from "@mui/material";
import defaultCard from "datalign-module-template/assets/search-cards/Circle.gif";
import { useEasierTheme } from "datalign-themes";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import SolrService from "services/SolrService";
import FacetFilter from "models/facet-filter";

// Helper function to dynamically import files
const importAll = (requireContext: __WebpackModuleApi.RequireContext) => {
  return requireContext.keys().map(requireContext);
};

// Dynamically load all images in the folder
const images = importAll(
  require.context(
    "datalign-module-template/assets/search-cards",
    false,
    /\.(png|jpe?g|gif)$/,
  ),
);

const { solrService } = new SolrService();

const FacetSearchCard = ({
  facetType,
  filter,
}: {
  facetType: string;
  filter: FacetFilter;
}) => {
  const easierTheme = useEasierTheme();
  const navigate = useNavigate();
  const abortRef = useRef<AbortController>();

  const [image, setImage] = useState<string>();
  const [defaultImage] = useState(
    () => images[Math.floor(Math.random() * images.length)] ?? defaultCard,
  );

  useEffect(() => {
    if (filter.has_image) {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      const signal = controller.signal;
      abortRef.current = controller;

      solrService
        .facet_image(facetType, filter.field, signal)
        .then((res) => setImage(res))
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.error(err);
            Toaster.makeToast(
              err?.message
                ? err.message
                : "An error occurred when searching boundary or point",
              "error",
            );
          }
        });
    }
  }, [filter]);

  return (
    <Card
      sx={{
        ...(["FAF-nt", "nrdata-orig"].includes(
          process.env.REACT_APP_STYLING_THEME ?? "",
        )
          ? {
              borderRadius: ".25rem",
              boxShadow:
                "0 4px 20px 1px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.08)",
              backgroundColor: "#f4f4f4",
            }
          : { backgroundColor: easierTheme?.Colour.SecondaryContainer() }),
        height: "275px",
      }}
    >
      <Tooltip title={filter.field}>
        <CardActionArea
          onClick={() => navigate(`/search?${facetType}=${filter.field}`)}
        >
          {
            <EzrImage
              src={image ?? defaultImage}
              style={{ objectFit: "fill", width: "100%", height: "175px" }}
            />
          }
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignContent: "space-between",
              height: "100px",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                textAlign: "left",
                ...(["FAF-nt", "nrdata-orig"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? {
                      color: "#1F1F5F",
                      letterSpacing: "0.3px",
                    }
                  : {}),
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: "1.1rem",
              }}
            >
              {filter.field}
            </Typography>
            <Typography
              color="initial"
              sx={{
                ...(["FAF-nt", "nrdata-orig"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? {
                      textAlign: "left",
                      fontFamily: "'proxima', sans-serif",
                      fontSize: "0.813rem",
                      fontWeight: 400,
                    }
                  : {}),
                display: "flex",
                m: "auto auto 0px 0px",
              }}
            >
              Number of datasets: {filter.count}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Tooltip>
    </Card>
  );
};

export default FacetSearchCard;
